import axios from 'axios';
import Qs from 'qs';
import { JSONAPIResponse, Meta } from '../types/api.types';
import { refreshGatehouseToken } from '../auth/gatehouse-oauth';

const RAW_BASE_URL =
  process.env['NX_PUBLIC_API_BASE_URL'] || 'http://localhost:8080';
const BASE_URL = RAW_BASE_URL.endsWith('/')
  ? RAW_BASE_URL.slice(0, -1)
  : RAW_BASE_URL;
console.log('🔧 API BASE_URL:', BASE_URL);
console.log(
  '🔧 Environment NX_PUBLIC_API_BASE_URL:',
  process.env['NX_PUBLIC_API_BASE_URL'],
);

// Dev helper: inject Bearer token for cross-origin dev against prod API.
// Usage: in browser console run  localStorage.setItem('dev_token', '<JWT>')
// The login interceptor below will capture it automatically from Set-Cookie
// when the response is not cross-origin, or you can paste it manually.
const DEV_TOKEN = process.env['NX_PUBLIC_DEV_TOKEN'] || null;

axios.interceptors.request.use((config) => {
  const token = DEV_TOKEN || localStorage.getItem('dev_token');
  if (token) {
    config.headers = config.headers || {};
    config.headers['Authorization'] = `Bearer ${token}`;
  }
  return config;
});

// Gatehouse access tokens expire in 15 minutes. On a 401 from Blanche,
// trade the stored refresh_token for a new access_token and replay the
// original request once. If the refresh fails (no refresh token, or
// Gatehouse rejected it), the 401 propagates to the caller as before
// — the user lands back on /login on the next render.
//
// Guard with `_gatehouseRetried` on the request config so a refreshed
// retry that *also* 401s doesn't trigger an infinite loop. Skip the
// flow entirely for the OAuth token endpoint itself; that one's
// failure is already handled in gatehouse-oauth.ts.
axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = error?.config;
    const status = error?.response?.status;
    if (
      status !== 401 ||
      !original ||
      original._gatehouseRetried ||
      String(original.url || '').includes('/oauth/token')
    ) {
      return Promise.reject(error);
    }

    original._gatehouseRetried = true;
    const fresh = await refreshGatehouseToken();
    if (!fresh) {
      return Promise.reject(error);
    }
    original.headers = original.headers || {};
    original.headers['Authorization'] = `Bearer ${fresh}`;
    return axios(original);
  },
);

const isObject = (object: any): boolean => {
  return object !== null && typeof object === 'object';
};

const deepEqual = (object1: any, object2: any): boolean => {
  const keys1 = Object.keys(object1);
  const keys2 = Object.keys(object2);
  if (keys1.length !== keys2.length) {
    return false;
  }
  for (const key of keys1) {
    const val1 = object1[key];
    const val2 = object2[key];
    const areObjects = isObject(val1) && isObject(val2);
    if (
      (areObjects && !deepEqual(val1, val2)) ||
      (!areObjects && val1 !== val2)
    ) {
      return false;
    }
  }
  return true;
};

export interface QueryRunResult<T = any> {
  data: ModelCollection | Model | null;
  getData: () => ModelCollection | Model | null;
}

export class Query {
  baseUrl: string = BASE_URL || '';
  httpClient: any = axios;
  httpMethod = 'get';
  pathTemplate: string | null = null;
  pathParams: any = null;
  data: any = null;
  id: any = null;
  filters: any = null;
  sort: any = null;
  limit: any = null;
  timeout = 60000;
  _include: any[] = [];
  _page: number | null = null;
  _size: number | null = null;

  constructor(pathTemplate?: string, pathParams?: any, options?: any) {
    if (pathTemplate) this.pathTemplate = pathTemplate;
    if (pathParams) this.pathParams = pathParams;
    if (options) {
      if (options.timeout !== undefined) {
        this.timeout = options.timeout;
      }

      if (options.httpMethod !== undefined) {
        this.httpMethod = options.httpMethod;
      }
    }
  }

  get(id: any) {
    this.id = id;
    return this.run();
  }

  page(page: number) {
    this._page = page;
    return this;
  }

  size(size: number) {
    this._size = size;
    return this;
  }

  sort_by(fieldname: string, desc = false) {
    if (!this.sort) {
      this.sort = {};
    }
    this.sort[fieldname] = desc;
    return this;
  }

  include(...args: any[]) {
    this._include = this._include.concat(args);
    return this;
  }

  filter(filters: any) {
    this.filters = Object.assign({}, this.filters, filters);
    return this;
  }

  setData(data: any, id: any) {
    this.data = data;

    if (id) {
      this.id = id;
    }

    if (this.httpMethod == 'get') {
      this.httpMethod = 'post';
    }

    return this;
  }

  setMethod(httpMethod: string) {
    this.httpMethod = httpMethod;
    return this;
  }

  getURL() {
    let path_str = this.pathTemplate;
    for (const prop in this.pathParams) {
      path_str = path_str?.replace(`{${prop}}`, this.pathParams[prop]) || null;
    }

    if (this.id) {
      path_str = path_str + `/${this.id}`;
    }

    // Ensure proper URL construction with forward slash
    const baseUrl = this.baseUrl.endsWith('/')
      ? this.baseUrl.slice(0, -1)
      : this.baseUrl;
    const pathStr = path_str?.startsWith('/') ? path_str : `/${path_str}`;

    return baseUrl + pathStr;
  }

  getQueryParams(): Record<string, any> {
    const params: Record<string, any> = {};
    if (this._page !== null) {
      params['page[number]'] = this._page;
    }
    if (this._size !== null) {
      params['page[size]'] = this._size;
    }
    if (this['sort']) {
      const sorts: string[] = [];
      for (const fieldname in this['sort']) {
        let sortStr = '';
        if ((this['sort'] as Record<string, any>)[fieldname]) {
          sortStr += '-';
        }
        sortStr += fieldname;
        sorts.push(sortStr);
      }
      if (sorts.length > 0) {
        params['sort'] = sorts.join(',');
      }
    }
    if (this['_include'] && (this['_include'] as any[]).length > 0) {
      params['include'] = (this['_include'] as any[]).join(',');
    }
    if (this.filters) {
      for (const attribute in this.filters) {
        params[`filter[${attribute}]`] = this.filters[attribute];
      }
    }
    return params;
  }

  getData() {
    return {
      data: this.data,
    };
  }

  async run<T>(): Promise<ModelCollection | Model | null> {
    if (!this.pathTemplate) return null;

    const path_str = this.getURL();

    const response = await this.httpClient.request({
      url: path_str,
      method: this.httpMethod,
      params: this.getQueryParams(),
      data: this.getData(),
      timeout: this.timeout,
      withCredentials: true,
      paramsSerializer: (params: Record<string, any>) =>
        Qs.stringify(params, { arrayFormat: 'repeat' }),
    });
    return this.processResponse(response);
  }

  private processResponse(response: any): ModelCollection | Model | null {
    if (response.status > 299) {
      throw Error('Non-200 response');
    }

    // TODO: handle errors

    if (response.status === 204) {
      return null;
    }

    const data = response.data.data || response.data;
    const included = response.data.included;
    const includedMap: Record<string, Record<string, any>> = {};

    if (included) {
      for (const include of included) {
        if (!includedMap[include.type]) {
          includedMap[include.type] = {};
        }
        const includedModel = new Model();
        includedModel.fromAPI(include);
        includedMap[include.type][include.id] = includedModel;
      }
    }

    if (Array.isArray(data)) {
      const out = new ModelCollection();
      out.setMeta(response.data.meta);
      const outData = [];
      for (const item of data) {
        const newModel = new Model();
        newModel.fromAPI(item, response.links, includedMap);
        outData.push(newModel);
      }
      out.setData(outData);
      return out;
    }

    const newModel = new Model();
    newModel.fromAPI(data, response.links, includedMap);

    return newModel;
  }
}

export class ModelCollection {
  data: any[];
  meta?: Meta;
  constructor() {
    this.data = [];
  }
  setMeta(meta: Meta) {
    Object.defineProperty(this, 'meta', { value: meta });
  }
  setData(data: any[]) {
    this.data = data;
  }
  getData(): any[] {
    return this.data;
  }
  concatCollection(otherCollection: ModelCollection) {
    this.data = this.data.concat(otherCollection.getData());
    if (this.meta && otherCollection.meta) {
      this.meta.count += otherCollection.meta.count;
    }
  }
}

export class Model {
  [key: string]: any;
  apiType?: string;
  oldAttributes?: Record<string, any>;
  relationships?: Record<string, any>;
  links?: Record<string, any>;
  topLevelLinks?: Record<string, any>;
  included?: Record<string, Record<string, any>>;
  id?: string;
  constructor(apiType?: string, data?: Record<string, any>) {
    if (apiType) {
      this.setAPIType(apiType);
    }
    if (data) {
      for (const prop in data) {
        this[prop] = data[prop];
      }
      if ('id' in data) {
        this.id = data['id'];
      }
    }
  }
  setAPIType(apiType: string) {
    Object.defineProperty(this, 'apiType', { value: apiType });
  }
  fromAPI(data: any, links?: any, included?: any) {
    if (!this.apiType) {
      this.setAPIType(data.type);
    }
    this.id = data.id;
    Object.defineProperty(this, 'id', {
      value: data.id,
      writable: true,
      configurable: true,
    });
    Object.defineProperty(this, 'oldAttributes', { value: data.attributes });
    Object.defineProperty(this, 'relationships', { value: data.relationships });
    Object.defineProperty(this, 'links', { value: data.links });
    Object.defineProperty(this, 'topLevelLinks', { value: links });
    Object.defineProperty(this, 'included', { value: included });
    for (const prop in data.attributes) {
      this[prop] = data.attributes[prop];
    }
    if (this.relationships) {
      for (const relationshipName in this.relationships) {
        Object.defineProperty(this, relationshipName, {
          get: () => {
            return this.getIncluded(relationshipName);
          },
        });
      }
    }
  }
  toAPI() {
    const attributes: Record<string, any> = {};
    for (const prop in this) {
      if (
        !this.oldAttributes ||
        this.oldAttributes[prop] !== this[prop] ||
        (isObject(this.oldAttributes[prop]) &&
          isObject(this[prop]) &&
          !deepEqual(this.oldAttributes[prop], this[prop]))
      ) {
        attributes[prop] = this[prop];
      }
    }
    const obj: any = {
      type: this.apiType,
      attributes,
    };
    if (this.id) {
      obj.id = this.id;
    }
    return obj;
  }
  getIncluded(relationshipName: string) {
    if (!this.relationships || !this.included) return undefined;
    const relationshipData = this.relationships[relationshipName]?.data;
    if (!relationshipData) return undefined;
    if (Array.isArray(relationshipData)) {
      const out = [];
      for (const item of relationshipData) {
        if (
          (this.included as Record<string, any>)[item.type] &&
          (this.included as Record<string, any>)[item.type][item.id]
        ) {
          out.push((this.included as Record<string, any>)[item.type][item.id]);
        }
      }
      return out;
    }
    if ((this.included as Record<string, any>)[relationshipData.type]) {
      return (this.included as Record<string, any>)[relationshipData.type][
        relationshipData.id
      ];
    }
    return undefined;
  }
}
